/**
 * What each look (08 §4) does to the frame beyond the stage and the figures: how hard the picture is
 * locked to the palette and dithered, and the colours of what is drawn outside the LUT - the side panels
 * and banners on the HUD canvas, and the screen transition. In the 1987 look every one of them is one of
 * the C64's sixteen, so the whole screen holds to them.
 */

import { type Data3DTexture, Vector2, type WebGLRenderer } from 'three';
import type { LevelDef } from '../../levels/types';
import type { DisplaySettings } from '../../settings';
import type { HudColours } from '../../ui/hud/panels';
import { levelLookFor, type Look } from '../levelLook';
import { rgb8ToHex } from '../color';
import { C64_COLOURS, c64Lut, type Grade, nearestC64, Palette } from '../palette';
import type { TransitionColours } from '../pipeline/RenderPipeline';
import { fitRenderSize, type RenderSize } from '../size';

/** The display as the game starts: the modern look, sharp, no aids (the faithful defaults, 08 §5). */
export const DISPLAY_DEFAULTS: DisplaySettings = {
  look: 'modern', crt: false, paletteLock: 0.25, pixelSize: 1, colourAid: false, reduceFlashing: false,
};

/** Dither strength: a light touch in the modern look; full strength in 1987's, as look-dev set it. */
const DITHER: Readonly<Record<Look, number>> = { modern: 0.03, 1987: 0.16 };

/** The modern look's vignette; 1987's has none, which would darken its sixteen colours into others. */
const VIGNETTE = 0.12;

/**
 * The palette lock, dither and vignette the pipeline draws with. The 1987 look is always locked all the
 * way (the CRT option's own vignette still applies: that is the display, not the picture).
 */
export function lockFor(d: DisplaySettings): { paletteLock: number; ditherAmp: number; vignette: number } {
  const c64 = d.look === '1987';
  const paletteLock = c64 ? 1 : Math.min(1, Math.max(0, d.paletteLock));
  return { paletteLock, ditherAmp: DITHER[d.look], vignette: c64 ? 0 : VIGNETTE };
}

const toLook = (look: Look, hex: string): string => (look === '1987' ? nearestC64(hex) : hex);

/** The side panels' colours, from the figures' palette (in 1987's, the players' own sprite colours). */
export function hudColoursFor(look: Look, p: Palette): HudColours {
  const own = look === '1987' ? 2 : 3;
  return {
    p1: toLook(look, p.hex(`bub.${own}`)),
    p2: toLook(look, p.hex(`bob.${own}`)),
    text: toLook(look, p.hex('paper.0')),
    dim: toLook(look, p.hex('metal.1')),
    accent: toLook(look, p.hex('hudAccent.0')),
  };
}

/** The banners' shadow ink. */
export const inkFor = (look: Look, p: Palette): string => toLook(look, p.hex('ink.0'));

/** C64 colour numbers of the 1987 transition: black bodies, light green rims, green walls, a white shine. */
const C64_TRANSITION = { fill: 0, rim: 13, wall: 5, shine: 1 } as const;

const c64Hex = (index: number): string => {
  const c = C64_COLOURS[index];
  return c ? rgb8ToHex(c) : '#000000';
};

/** The transition's bubbles: dark bodies in green rims, like the glass in play, with a white shine. */
export function transitionColoursFor(look: Look, p: Palette): TransitionColours {
  if (look === '1987') {
    const t = C64_TRANSITION;
    return { fill: c64Hex(t.fill), rim: c64Hex(t.rim), wall: c64Hex(t.wall), shine: c64Hex(t.shine) };
  }
  return {
    fill: toLook(look, p.hex('ink.1')),
    rim: toLook(look, p.hex('bubble.2')),
    wall: toLook(look, p.hex('bubble.0')),
    shine: toLook(look, p.hex('bubble.3')),
  };
}

/** The largest internal size that fits the renderer's drawing buffer at `pixelSize` screen pixels a texel. */
export function fitDisplay(renderer: WebGLRenderer, pixelSize: number): RenderSize {
  const b = renderer.getDrawingBufferSize(new Vector2());
  return fitRenderSize(b.x, b.y, pixelSize);
}

/** The figures' palette (the title's too): the level's look in the look's figure colours. */
export const figurePaletteFor = (def: LevelDef, look: Look): Palette =>
  new Palette(levelLookFor(def, look), look === '1987' ? 'c64' : 'arcade');

/** How much colour the glass's clear middle holds (materials/glassBubble.ts `glassBody`): none in 1987's. */
export const glassBodyFor = (look: Look): number => (look === '1987' ? 0 : 0.05);

/**
 * The LUT for a level (both levels' during a scroll, `next`): the level's own palette, or in the 1987
 * look the sixteen C64 colours whatever the level (shared: never dispose `c64Lut()`).
 */
export function lutFor(look: Look, def: LevelDef, next: LevelDef | null): Data3DTexture {
  if (look === '1987') return c64Lut();
  const extra = next ? new Palette(levelLookFor(next, look)).all() : [];
  return new Palette(levelLookFor(def, look)).buildLut(extra);
}

/** The grade: no fog in 1987's black cave (it comes after the lock, so it would add colours the C64 never had). */
export const gradeFor = (look: Look, grade: Grade): Grade => (look === '1987' ? { ...grade, fogAmount: 0 } : grade);
